import { Container, Graphics } from 'pixi.js';
import gsap from 'gsap';
import { NEON_COLORS } from '../colors';
import type { ColorType } from '@shared/types';

/**
 * Tap feedback — fires on every correct grab, so it's the most-seen FX in the
 * game. A neon core flash + expanding ring + a few radial sparks, all on an
 * additive-blend layer so the colors bloom. Everything self-destructs; no
 * pooling needed at this volume (≤1 per tap, ~2-3 concurrent worst case).
 */
export function spawnBurst(
  parent: Container,
  x: number,
  y: number,
  color: ColorType,
  scale = 1,
): void {
  const c = NEON_COLORS[color];
  const layer = new Container();
  layer.x = x;
  layer.y = y;
  layer.scale.set(scale); // grows with combo for escalating tap feedback
  layer.blendMode = 'add';
  parent.addChild(layer);

  // Bright core flash — pops big, collapses fast.
  const core = new Graphics();
  core.circle(0, 0, 18).fill({ color: c.light, alpha: 1 });
  core.scale.set(0.4);
  layer.addChild(core);
  gsap.to(core.scale, { x: 1.6, y: 1.6, duration: 0.22, ease: 'power3.out' });
  gsap.to(core, { alpha: 0, duration: 0.28, ease: 'power2.out' });

  // Expanding hollow ring — the lingering shock.
  const ring = new Graphics();
  layer.addChild(ring);
  const rs = { r: 10, a: 0.85, w: 5 };
  gsap.to(rs, {
    r: 52,
    a: 0,
    w: 1,
    duration: 0.5,
    ease: 'power2.out',
    onUpdate: () => {
      if (ring.destroyed) return;
      ring.clear().circle(0, 0, rs.r).stroke({ width: rs.w, color: c.base, alpha: rs.a });
    },
  });

  // Radial sparks shooting outward — a few more at higher combo (bigger scale).
  const sparkCount = Math.round(6 + Math.max(0, scale - 1) * 8);
  for (let i = 0; i < sparkCount; i++) {
    const a = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.5;
    const dist = 28 + Math.random() * 22;
    const spark = new Graphics();
    spark.circle(0, 0, 2.5 + Math.random() * 1.5).fill({ color: c.light, alpha: 1 });
    layer.addChild(spark);
    gsap.to(spark, {
      x: Math.cos(a) * dist,
      y: Math.sin(a) * dist,
      alpha: 0,
      duration: 0.34 + Math.random() * 0.12,
      ease: 'power2.out',
    });
  }

  // Tear the whole layer down once the longest tween (ring, 0.5s) finishes.
  gsap.delayedCall(0.62, () => {
    if (!layer.destroyed) layer.destroy({ children: true });
  });
}
